<script setup>
import { keyBy } from 'lodash-es';
import { inject, onBeforeUnmount } from 'vue';
import { useAuthStore } from '~/auth/stores/auth.store';
import BiGlobalFiltersDateIntervalBin from '~/bi/components/filters/bi-global-filters/bi-global-filters-date-interval-bin.vue';
import { useBIDatasetPicker } from '~/bi/composables/useBIDatasetPicker.js';
import { useBIFilterMaps } from '~/bi/composables/useBIFilterMaps';
import { OPERATORS_CONFIG_MAP } from '~/bi/constants/bi-constants';
import { useBiStore } from '~/bi/store/bi.store';
import { getAllOperatorsConfig, getOperatorValue } from '~/bi/utils/bi-filter-helper.utils';

// --------------------------------- Imports -------------------------------- //

// ---------------------------------- Props --------------------------------- //
const props = defineProps({
  filterIndex: {
    type: Number,
    required: true,
  },
});

// ---------------------------------- Emits --------------------------------- //

// ---------------------------- Injects/Provides ---------------------------- //
const global_filters_of_component = inject('global_filters_of_component');

// ----------------------- Variables - Pinia - consts ----------------------- //
const { getTypeOperatorMap, getOperatorComponent } = useBIFilterMaps();
const auth_store = useAuthStore();

// --------------------- Variables - Pinia - storeToRefs -------------------- //

// ------------------- Variables - Local - consts and lets ------------------ //
const type_operator_map = getTypeOperatorMap();
const bi_store = useBiStore();
const all_operators = getAllOperatorsConfig();

const filter_types = [
  { label: 'Text', value: 'text' },
  { label: 'Number', value: 'number' },
  { label: 'Date range', value: 'date_range' },
  { label: 'Date interval', value: 'date_interval' },
];

// ------------------------ Variables - Local - refs ------------------------ //
const form$ = ref(null);
const is_filter_settings_open = ref(false);
const column_options = ref([]);
const selected_column = ref(null);
const selected_table = ref(null);

// ---------------------- Variables - Local - reactives --------------------- //

// --------------------------- Computed properties -------------------------- //
const datasets_map = computed(() => keyBy(bi_store.all_schemas, 'name'));

const { hierarchicalSchemas } = useBIDatasetPicker(bi_store.all_schemas);

const filter_type_map_operator = computed(() => {
  const type = form$.value?.data?.filter_type;
  if (type === 'text')
    return 'text';
  if (type === 'number')
    return 'integer';
  if (['date_range', 'date_interval'].includes(type))
    return 'date';
  return 'text';
});

const supported_types = computed(() => {
  const type = form$.value?.data?.filter_type;
  if (type === 'text')
    return ['text'];
  if (type === 'number')
    return ['integer'];
  if (['date_range', 'date_interval'].includes(type))
    return ['date', 'timestamp'];
  return [];
});

const available_operators = computed(() => type_operator_map[filter_type_map_operator.value].map(operator_name => OPERATORS_CONFIG_MAP[operator_name]));
const selected_operator = ref(global_filters_of_component.value[props.filterIndex]?.general?.default_filter_config?.op ? all_operators[global_filters_of_component.value[props.filterIndex]?.general?.default_filter_config?.op] : (available_operators.value[0] || null));
const current_component = computed(() => {
  const column_config = {};

  if (selected_column.value?.field) {
    column_config.is_table_field = true;
    column_config.table_name = selected_table.value.name;
    column_config.name = global_filters_of_component.value[props.filterIndex]?.general.column_name || selected_column.value.label;
  }
  return getOperatorComponent({
    selected_operator: selected_operator.value,
    selected_column_config: { type: filter_type_map_operator.value, ...column_config },
  });
});

// -------------------------------- Functions ------------------------------- //
function onSelectOperator(operator, close = () => {}) {
  selected_operator.value = operator;
  close?.();
};

function getFormattedOperatorValue(data) {
  const user_timezone = auth_store?.logged_in_user_details?.timezone;
  return getOperatorValue(data, selected_operator.value, filter_type_map_operator.value, user_timezone);
}

function clearWidgets() {
  global_filters_of_component.value[props.filterIndex].widgets = {};
}

function onFilterNameChange(filter_name) {
  global_filters_of_component.value[props.filterIndex].general.filter_name = filter_name;
}

function onFilterTypeChange(filter_type) {
  selected_column.value = null;
  selected_table.value = null;
  form$.value.el$('set_default_filter_value')?.reset();
  clearWidgets();

  global_filters_of_component.value[props.filterIndex].general.filter_type = filter_type;
  global_filters_of_component.value[props.filterIndex].general.default_filter_config = {};
  global_filters_of_component.value[props.filterIndex].general.column = null;
  global_filters_of_component.value[props.filterIndex].general.column_name = null;
  global_filters_of_component.value[props.filterIndex].general.dataset = null;
  selected_operator.value = available_operators.value[0] || null;
}

function onDatasetChange(dataset_value) {
  selected_column.value = null;
  selected_table.value = dataset_value;
  column_options.value = dataset_value.columns;
  global_filters_of_component.value[props.filterIndex].general.dataset = dataset_value.name;
}

function onColumnChange(column_config) {
  selected_column.value = column_config;
  global_filters_of_component.value[props.filterIndex].general.column = selected_column.value.field;
  const column_name = selected_table.value?.columns.find(col => col.field === selected_column.value.field)?.name;
  global_filters_of_component.value[props.filterIndex].general.column_name = column_name;
}
function onDefaultFilterValueChange(value) {
  global_filters_of_component.value[props.filterIndex].general.set_default_filter_value = value;

  if (!value) {
    global_filters_of_component.value[props.filterIndex].general.default_filter_config = {};
  }
}
function onSingleSelectValueChange(value) {
  global_filters_of_component.value[props.filterIndex].general.set_single_select_value = value;
}

function onDefaultFilterConfigChange() {
  if (!global_filters_of_component.value.length || !global_filters_of_component.value[props.filterIndex]?.general || !global_filters_of_component.value[props.filterIndex]?.general?.set_default_filter_value) {
    return;
  }

  const default_filter_config = {
    ...form$?.value?.data,
    op: form$?.value?.data.operator_name || selected_operator.value.name,
    operator_value: getFormattedOperatorValue(form$?.value?.data),
  };

  if (form$?.value?.data.filter_type === 'date_interval') {
    delete default_filter_config.op;
  }

  delete default_filter_config.filter_name;
  delete default_filter_config.filter_type;
  delete default_filter_config.dataset;
  delete default_filter_config.column;
  delete default_filter_config.set_default_filter_value;
  delete default_filter_config.set_single_select_value;

  global_filters_of_component.value[props.filterIndex].general.default_filter_config = default_filter_config;
}

// -------------------------------- Watchers -------------------------------- //

// ----------------------------- Lifecycle Hooks ---------------------------- //
onMounted(() => {
  selected_table.value = global_filters_of_component.value[props.filterIndex]?.general?.dataset ? datasets_map.value[global_filters_of_component.value[props.filterIndex]?.general?.dataset] : null;
  column_options.value = global_filters_of_component.value[props.filterIndex]?.general?.dataset ? datasets_map.value[global_filters_of_component.value[props.filterIndex]?.general?.dataset]?.columns : [];
  selected_column.value = global_filters_of_component.value[props.filterIndex]?.general?.column ? column_options.value.find(column => column.field === global_filters_of_component.value[props.filterIndex]?.general?.column) : null;
});

onBeforeUnmount(() => {
  onDefaultFilterConfigChange();
});
</script>

<template>
  <Vueform
    ref="form$"
    size="sm"
    :display-errors="false"
  >
    <!-- Body -->
    <div class="col-span-12 h-[21rem] scrollbar">
      <!-- General -->
      <div class="flex w-full gap-4">
        <TextElement
          class="w-1/2"
          :add-classes="{
            ElementLayout: {
              outerWrapper: '!block',
            },
            ElementLabel: {
              container: 'mb-2',
            },
          }"
          rules="required"
          :label="$t('Name')"
          name="filter_name"
          :default="global_filters_of_component[props.filterIndex]?.general?.filter_name"
          @change="onFilterNameChange"
        />
        <SelectElement
          class="w-1/2"
          :add-classes="{
            ElementLayout: {
              outerWrapper: '!block',
            },
            ElementLabel: {
              container: 'mb-2',
            },
          }"
          rules="required"
          :label="$t('Type')"
          name="filter_type"
          :items="filter_types"
          :native="false"
          :can-clear="false"
          :can-deselect="false"
          :default="global_filters_of_component[props.filterIndex]?.general?.filter_type"
          @select="onFilterTypeChange"
        />
      </div>
      <div v-if="form$?.data?.filter_type === 'text'" class="flex w-full gap-4 mt-2">
        <div class="flex flex-col gap-2 w-1/2">
          <span class="text-sm">
            {{ $t('Dataset') }}
          </span>
          <bi-query-builder-table-picker
            :hierarchical-schemas="hierarchicalSchemas"
            :model-value="selected_table"
            @update:model-value="onDatasetChange"
          />
        </div>
        <div class="flex flex-col gap-2 w-1/2">
          <span class="text-sm">
            {{ $t('Column') }}
          </span>
          <HawkMenu
            additional_trigger_classes="border border-gray-300 !px-0 !py-0 w-full"
            :items="[]"
            additional_dropdown_classes="!mb-0"
            position="fixed"
          >
            <template #trigger="{ open }">
              <div class="px-3 py-2 w-full flex items-center flex-1 gap-2 text-sm">
                <span class="w-full text-left">
                  {{ selected_column?.label }}
                </span>
                <div>
                  <IconHawkChevronUp v-if="open" />
                  <IconHawkChevronDown v-else />
                </div>
              </div>
            </template>
            <template #content="{ close: closeOperatorMenu }">
              <div v-if="selected_table" class="bg-white p-1 rounded-lg">
                <bi-query-builder-column-picker
                  :tables="datasets_map[selected_table.name] ? [datasets_map[selected_table.name]] : []"
                  :has-functions="false"
                  :show-stage-header="false"
                  :supported-types="supported_types"
                  @selected="(column_config) => { onColumnChange(column_config); closeOperatorMenu() }"
                />
              </div>
              <div v-else class="p-2 w-[240px] text-gray-500 text-sm">
                {{ $t('No columns available') }}
              </div>
            </template>
          </HawkMenu>
        </div>
      </div>
      <!-- Filter Settings -->
      <div class="w-full">
        <div class="flex mt-2">
          <div class="flex justify-center items-center gap-2" @click="is_filter_settings_open = !is_filter_settings_open">
            {{ $t('Filter settings') }}
            <!-- <IconHawkChevronUp v-if="is_filter_settings_open" />
            <IconHawkChevronDown v-else /> -->
          </div>
        </div>
        <div>
          <!-- {{ selected_column?.label }} -->
          <CheckboxElement
            v-if="selected_column?.label"
            name="set_single_select_value"
            class="mt-2 w-2/3"
            :default="global_filters_of_component[props.filterIndex]?.general?.set_single_select_value"
            @change="onSingleSelectValueChange"
          >
            {{ $t('Limit selection to a single value') }}
          </CheckboxElement>
          <CheckboxElement
            name="set_default_filter_value"
            class="mt-2 w-1/3"
            :default="global_filters_of_component[props.filterIndex]?.general?.set_default_filter_value"
            @change="onDefaultFilterValueChange"
          >
            {{ $t('Default value') }}
          </CheckboxElement>
          <div v-if="form$?.data?.set_default_filter_value">
            <!-- Header -->
            <div v-if="form$?.data?.filter_type !== 'date_interval'" class="flex items-center justify-between">
              <HawkMenu
                additional_trigger_classes="!ring-0 !focus:ring-0 !px-0 !py-0"
                :items="available_operators"
                additional_dropdown_classes="!mb-0"
                position="fixed"
              >
                <template #trigger="{ open }">
                  <div class="pt-3 pb-2 w-full flex items-center flex-1 gap-2 text-sm">
                    {{ selected_operator.label }}
                    <div>
                      <IconHawkChevronUp v-if="open" />
                      <IconHawkChevronDown v-else />
                    </div>
                  </div>
                </template>
                <template #content="{ close: closeOperatorMenu }">
                  <div class="bg-white p-1 rounded-lg max-h-60 scrollbar">
                    <div
                      v-for="operator in available_operators" :key="operator.name"
                      class="p-2 text-sm font-medium min-w-[10rem] rounded-lg cursor-pointer text-gray-700 hover:bg-gray-50 flex items-center  mt-2"
                      @click="onSelectOperator(operator, closeOperatorMenu)"
                    >
                      {{ operator.label }}
                      <IconHawkCheck v-if="selected_operator.name === operator.name" class="ml-auto text-primary-500 w-5 h-5 min-w-5" />
                    </div>
                  </div>
                </template>
              </HawkMenu>
            </div>
            <!-- Operator config -->
            <div v-if="form$?.data?.filter_type !== 'date_interval'" class="mb-2 max-h-96">
              <component
                :is="current_component.component"
                :key="`${selected_operator.name}_${filter_type_map_operator}_${selected_column?.label || ''}_${form$?.data?.set_single_select_value}`"
                class="col-span-12 p-2 pt-0"
                v-bind="{
                  componentConfig: { ...current_component.componentConfig, single_select: form$?.data?.set_single_select_value },
                  columnConfig: current_component.columnConfig,
                  filterConfig: { ...(global_filters_of_component[props.filterIndex].general.default_filter_config || {}), operator: selected_operator },
                  operatorConfig: current_component.operatorConfig,
                  formData: form$?.data,
                  formElements: form$?.elements$,
                }"
              />
            </div>
            <div v-if="form$?.data?.filter_type === 'date_interval'" class="my-2 max-h-96">
              <BiGlobalFiltersDateIntervalBin :filter-config="{ ...(global_filters_of_component[props.filterIndex].general.default_filter_config || {}) }" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Vueform>
</template>

<style scoped lang="scss">

</style>
